"use client"

import { useState, useEffect } from "react"
import { useParams, useRouter } from "next/navigation"
import Link from "next/link"
import { Calendar, ArrowLeft, Newspaper } from "lucide-react"

interface NewsArticle {
    nid: number
    img: string | null
    post_date: number
    title_pt: string
    title_en: string | null
    content_pt: string
    content_en: string | null
}

export default function NewsDetailPage() {
    const params = useParams()
    const router = useRouter()
    const [article, setArticle] = useState<NewsArticle | null>(null)
    const [loading, setLoading] = useState(true)
    const [notFound, setNotFound] = useState(false)

    useEffect(() => {
        const fetchArticle = async () => {
            try {
                const res = await fetch(`/api/news/${params.id}`)
                if (res.ok) {
                    setArticle(await res.json())
                } else if (res.status === 404) {
                    setNotFound(true)
                }
            } catch {
                console.error("Failed to fetch article")
            } finally {
                setLoading(false)
            }
        }
        if (params.id) fetchArticle()
    }, [params.id])

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900">
                <div className="container mx-auto px-4 py-16 max-w-4xl">
                    <div className="animate-pulse space-y-6">
                        <div className="h-6 bg-slate-800/50 rounded w-32" />
                        <div className="h-10 bg-slate-800/50 rounded w-3/4" />
                        <div className="h-4 bg-slate-800/50 rounded w-40" />
                        <div className="h-64 bg-slate-800/50 rounded-2xl" />
                        <div className="space-y-3">
                            <div className="h-4 bg-slate-800/50 rounded w-full" />
                            <div className="h-4 bg-slate-800/50 rounded w-full" />
                            <div className="h-4 bg-slate-800/50 rounded w-2/3" />
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (notFound || !article) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <Newspaper className="w-20 h-20 text-slate-700 mx-auto mb-6" />
                    <h1 className="text-3xl font-black text-white mb-2">Article Not Found</h1>
                    <p className="text-slate-400 mb-8">This news article doesn&apos;t exist or has been removed.</p>
                    <Link
                        href="/news"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white font-bold rounded-xl hover:bg-blue-400 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to News
                    </Link>
                </div>
            </div>
        )
    }

    const title = article.title_en || article.title_pt
    const content = article.content_en || article.content_pt

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900">
            <div className="container mx-auto px-4 py-16 max-w-4xl">
                {/* Back link */}
                <Link
                    href="/news"
                    className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm font-bold uppercase tracking-widest mb-8 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to News
                </Link>

                {/* Header */}
                <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-4 leading-tight">
                    {title}
                </h1>

                <div className="flex items-center gap-2 text-slate-500 text-sm font-bold uppercase tracking-widest mb-8">
                    <Calendar className="w-4 h-4" />
                    {new Date(article.post_date * 1000).toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                    })}
                </div>

                {/* Hero image */}
                {article.img && (
                    <div className="rounded-2xl overflow-hidden mb-10 border border-slate-800/50">
                        <img
                            src={article.img}
                            alt={title}
                            className="w-full h-auto max-h-[500px] object-cover"
                        />
                    </div>
                )}

                {/* Content */}
                <div
                    className="prose prose-invert prose-lg max-w-none
                        prose-headings:font-black prose-headings:tracking-tight
                        prose-p:text-slate-300 prose-p:leading-relaxed
                        prose-a:text-blue-400 prose-a:no-underline hover:prose-a:text-blue-300
                        prose-strong:text-white
                        prose-img:rounded-xl prose-img:border prose-img:border-slate-800/50"
                    dangerouslySetInnerHTML={{ __html: content }}
                />

                {/* Footer */}
                <div className="mt-16 pt-8 border-t border-slate-800/50">
                    <Link
                        href="/news"
                        className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm font-bold uppercase tracking-widest transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to News
                    </Link>
                </div>
            </div>
        </div>
    )
}
